import { isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { getDefaultPartner } from '~/features/admin/components/partner/utils'
import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import useGetPartnerConfigByDomain from '~/features/partners/hooks/useGetPartnerConfigByDomain'
import useGetPartnerConfigByOrgId from '~/features/partners/hooks/useGetPartnerConfigByOrgId'
import { TPartner } from '~/features/partners/models'
import { EAdminPortalClientPath, EMarketingClientPath } from '~/models/api'
import { sessionStore, store } from '~/store'

const usePartnerCentralized = () => {
  const location = useLocation()
  const user = store((state) => state.user)

  const isLoadingUser = sessionStore((state) => state.UIElements.isLoadingUser)

  const { isSuccess: isSuccessAuthData } = useGetAuthSessionQuery()

  const isNonPartnerPath = useMemo(
    () =>
      Object.values(EMarketingClientPath).includes(
        location.pathname as EMarketingClientPath,
      ) ||
      Object.values(EAdminPortalClientPath).includes(
        location.pathname as EAdminPortalClientPath,
      ),
    [location.pathname],
  )

  const shouldGetPartnerByOrgId = !(isNonPartnerPath || !user.organizationId)

  const {
    data: partnerByOrgIdResponse,
    isError: isErrorByOrgId,
    isLoading: isLoadingByOrgId,
  } = useGetPartnerConfigByOrgId(
    shouldGetPartnerByOrgId ? user.organizationId : '',
  )

  const shouldGetPartnerByDomain =
    !isNonPartnerPath &&
    (isErrorByOrgId ||
      isLoadingUser || // this check is needed to know the partner ID on user registration.
      (!isLoadingByOrgId &&
        !partnerByOrgIdResponse?.data &&
        !user.organizationId))

  const { data: partnerDomainResponse, isLoading: isLoadingByDomain } =
    useGetPartnerConfigByDomain(shouldGetPartnerByDomain)

  const [partner, setPartner] = useState<TPartner | null>(
    partnerByOrgIdResponse?.data ?? partnerDomainResponse?.data ?? null,
  )

  useEffect(() => {
    if (partnerByOrgIdResponse?.data || partnerDomainResponse?.data) {
      const newData =
        partnerByOrgIdResponse?.data ?? partnerDomainResponse?.data ?? null

      if (!isEqual(newData, partner)) {
        setPartner(newData)
      }
    }
  }, [partner, partnerByOrgIdResponse?.data, partnerDomainResponse?.data])

  useEffect(() => {
    if (isNonPartnerPath) {
      setPartner(null)
    }
  }, [isNonPartnerPath])

  const isLoading =
    !isSuccessAuthData || isLoadingByOrgId || isLoadingByDomain || isLoadingUser

  return useMemo(
    () => ({
      isLoading,
      partner: getDefaultPartner(partner),
      location,
    }),
    [isLoading, partner, location],
  )
}

export default usePartnerCentralized
